/**
 * <s-order-builder>
 * Custom pair builder: swatches, size drawers, summary, add to cart, and a mobile photo slider.
 *
 * - Swatches (`[data-order-option]`) live inside `[data-order-group="denim|thread|rivet"]`.
 *   The first swatch in each group starts selected. Hover or focus shows its name in the
 *   group's `[data-order-name]` slot; click selects it.
 * - Waist and inseam are `[data-order-size]` buttons inside `[data-order-size-group]`; they start on the smallest sizes and are optional.
 * - Summary slots `[data-order-value="denim|thread|rivet|waist|inseam|price"]` show the current picks.
 * - `[data-order-add]` adds the matching Waist/Inseam variant (or the first variant) to the
 *   cart with the picks as line item properties, then opens the cart page.
 * - "Begin my pair" stays aria-disabled, with the `[data-order-hint]` line shown, until both sizes are picked.
 * - `[data-order-thumb]` buttons swap their photo into `[data-order-main-img]`. The main photo and other
 *   `[data-order-zoom]` buttons open the `[data-order-lightbox]` dialog: arrows (on screen and keys) step
 *   through the photos; Escape, the X or a click outside the image closes it and focus returns to the photo.
 */
class SOrderBuilder extends HTMLElement {
  #ac;
  /** @type {Record<string, HTMLElement | null>} */
  #selected = { denim: null, thread: null, rivet: null };
  /** @type {Record<string, string>} */
  #sizes = { fit: '', waist: '', inseam: '' };

  /** @type {Record<string, string>} */
  #emptyNames = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const slot of this.querySelectorAll('[data-order-name]')) {
      const kind = slot.getAttribute('data-order-name');
      if (kind) this.#emptyNames[kind] = slot.textContent || '';
    }

    for (const group of this.querySelectorAll('[data-order-group]')) {
      const kind = group.getAttribute('data-order-group');
      if (!kind) continue;
      const options = Array.from(group.querySelectorAll('[data-order-option]'));
      if (options.length === 0) continue;
      this.#select(kind, options[0]);

      for (const option of options) {
        if (!(option instanceof HTMLElement)) continue;
        option.addEventListener('click', () => {
          this.#select(kind, option);
          this.#flashTip(option);
        }, opts);
        option.addEventListener('pointerenter', () => this.#paint(kind, option), opts);
        option.addEventListener('focus', () => this.#paint(kind, option), opts);
        option.addEventListener('pointerleave', () => this.#paint(kind, this.#selected[kind]), opts);
        option.addEventListener('blur', () => this.#paint(kind, this.#selected[kind]), opts);
      }
    }

    // Fixed picks with no buttons (like Fit) take their value from the label text.
    for (const fixed of this.querySelectorAll('[data-order-fixed]')) {
      const kind = fixed.getAttribute('data-order-fixed');
      if (kind) this.#sizes[kind] = (fixed.textContent || '').trim();
    }

    for (const sizeButton of this.querySelectorAll('[data-order-size]')) {
      if (!(sizeButton instanceof HTMLElement)) continue;
      const kind = sizeButton.dataset.orderSize || '';
      const restore = () => this.#showName(kind, this.#sizes[kind] || this.#emptyNames[kind] || '');
      // A button rendered pressed (like the single Fit option) starts as the pick.
      if (sizeButton.getAttribute('aria-pressed') === 'true') this.#sizes[kind] = sizeButton.dataset.value || '';
      sizeButton.addEventListener('click', () => this.#selectSize(sizeButton), opts);
      sizeButton.addEventListener('pointerenter', () => this.#showName(kind, sizeButton.dataset.value || ''), opts);
      sizeButton.addEventListener('focus', () => this.#showName(kind, sizeButton.dataset.value || ''), opts);
      sizeButton.addEventListener('pointerleave', restore, opts);
      sizeButton.addEventListener('blur', restore, opts);
    }

    const priceSlot = this.querySelector('[data-order-value="price"]');
    if (priceSlot) priceSlot.textContent = this.dataset.price || '';

    this.#updateReady();
    this.#setupLightbox(opts);

    const addButton = this.querySelector('[data-order-add]');
    if (addButton instanceof HTMLElement) addButton.addEventListener('click', () => this.#addToCart(addButton), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** @param {string} kind @param {HTMLElement} option */
  #select(kind, option) {
    this.#selected[kind] = option;
    const group = this.querySelector(`[data-order-group="${kind}"]`);
    group?.querySelectorAll('[data-order-option]').forEach((el) => {
      el.setAttribute('aria-pressed', el === option ? 'true' : 'false');
    });
    const slot = this.querySelector(`[data-order-value="${kind}"]`);
    if (slot) slot.textContent = option.dataset.label || '';
    this.#paint(kind, option);
  }

  /** Shows a swatch's name next to its group label (hover, focus, or the current pick).
   * @param {string} kind @param {HTMLElement | null} option */
  #paint(kind, option) {
    this.#showName(kind, option?.dataset.label || '');
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #tipTimer;

  /** Shows a swatch's name tag briefly after a tap, since touch screens have no hover.
   * @param {HTMLElement} option */
  #flashTip(option) {
    clearTimeout(this.#tipTimer);
    this.querySelectorAll('[data-order-option][data-tip]').forEach((el) => el.removeAttribute('data-tip'));
    option.setAttribute('data-tip', '');
    this.#tipTimer = setTimeout(() => option.removeAttribute('data-tip'), 1600);
  }

  /** @param {string} kind @param {string} text */
  #showName(kind, text) {
    const slot = this.querySelector(`[data-order-name="${kind}"]`);
    if (slot) slot.textContent = text;
  }

  #picks() {
    const label = (kind) => this.#selected[kind]?.dataset.label || '';
    return { denim: label('denim'), thread: label('thread'), rivet: label('rivet'), price: this.dataset.price || '' };
  }

  /** @param {HTMLElement} button */
  #selectSize(button) {
    const kind = button.dataset.orderSize;
    const value = button.dataset.value || '';
    if (!kind) return;
    this.#sizes[kind] = value;
    this.querySelectorAll(`[data-order-size="${kind}"]`).forEach((el) => {
      el.setAttribute('aria-pressed', el === button ? 'true' : 'false');
    });
    const slot = this.querySelector(`[data-order-value="${kind}"]`);
    if (slot) slot.textContent = value;
    this.#showName(kind, value);
    this.#updateReady();
  }

  #updateReady() {
    // Denim, thread and rivets are required; waistband and inseam are optional.
    const p = this.#picks();
    const ready = Boolean(this.#sizes.fit && p.denim && p.thread && p.rivet);
    const button = this.querySelector('[data-order-add]');
    if (button) button.toggleAttribute('aria-disabled', !ready);
    if (button && !ready) button.setAttribute('aria-disabled', 'true');
    const hint = this.querySelector('[data-order-hint]');
    if (hint instanceof HTMLElement) hint.hidden = ready;
    if (ready) this.#showError('');
  }

  /** @param {{ signal: AbortSignal }} opts */
  #setupLightbox(opts) {
    const dialog = this.querySelector('[data-order-lightbox]');
    const view = dialog?.querySelector('[data-order-lightbox-img]');
    const thumbs = Array.from(this.querySelectorAll('[data-order-thumb]'));
    const main = this.querySelector('[data-order-main]');
    const mainImg = this.querySelector('[data-order-main-img]');
    const others = Array.from(this.querySelectorAll('[data-order-zoom]:not([data-order-main])'));
    // Lightbox order: every thumbnail photo, then any other zoomable photos.
    const photos = [...thumbs, ...others].map((el) => el.querySelector('img')).filter(Boolean);

    /** Largest candidate from a photo's srcset, so large views stay sharp. */
    const sourceOf = (img) => {
      const candidates = (img.getAttribute('srcset') || '')
        .split(',')
        .map((part) => part.trim().split(/\s+/))
        .filter(([url]) => url)
        .map(([url, w]) => ({ url, w: parseInt(w, 10) || 0 }))
        .sort((a, b) => b.w - a.w);
      return candidates[0]?.url || img.currentSrc || img.src;
    };

    let active = 0;
    const choose = (i) => {
      const img = thumbs[i]?.querySelector('img');
      if (!img || !(mainImg instanceof HTMLImageElement)) return;
      active = i;
      thumbs.forEach((t, n) => t.setAttribute('aria-pressed', n === i ? 'true' : 'false'));
      mainImg.src = sourceOf(img);
      mainImg.alt = img.alt || '';
    };
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => choose(i), opts));
    if (thumbs.length > 0) choose(0);

    if (!(dialog instanceof HTMLDialogElement) || !(view instanceof HTMLImageElement) || photos.length === 0) return;

    let index = 0;
    /** @type {HTMLElement | null} */
    let opener = null;

    const show = (i) => {
      index = (i + photos.length) % photos.length;
      const img = photos[index];
      view.src = sourceOf(img);
      view.alt = img.alt || '';
    };

    const open = (i, trigger) => {
      opener = trigger;
      show(i);
      dialog.showModal();
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        dialog.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease-out' });
      }
      dialog.querySelector('[data-order-lightbox-close]')?.focus();
    };

    if (main instanceof HTMLElement) main.addEventListener('click', () => open(active, main), opts);
    others.forEach((trigger, j) => {
      if (trigger instanceof HTMLElement) trigger.addEventListener('click', () => open(thumbs.length + j, trigger), opts);
    });

    dialog.querySelector('[data-order-lightbox-close]')?.addEventListener('click', () => dialog.close(), opts);
    dialog.querySelector('[data-order-lightbox-prev]')?.addEventListener('click', () => show(index - 1), opts);
    dialog.querySelector('[data-order-lightbox-next]')?.addEventListener('click', () => show(index + 1), opts);

    // A click on the dark area (not the photo or a control) closes the viewer.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    }, opts);

    dialog.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(dialog).direction === 'rtl';
      if (event.key === 'ArrowRight') show(index + (rtl ? -1 : 1));
      else if (event.key === 'ArrowLeft') show(index + (rtl ? 1 : -1));
      else if (event.key === 'Tab') {
        // Keep focus on the viewer's own controls.
        const controls = Array.from(dialog.querySelectorAll('button'));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }, opts);

    dialog.addEventListener('close', () => {
      view.src = 'data:,';
      opener?.focus();
    }, opts);
  }

  #showError(message) {
    const error = this.querySelector('[data-order-error]');
    if (error) error.textContent = message || '';
  }

  /** @returns {number | null} */
  #variantId() {
    try {
      const variants = JSON.parse(this.querySelector('[data-order-variants]')?.textContent || '[]');
      const names = JSON.parse(this.querySelector('[data-order-option-names]')?.textContent || '[]');
      if (!Array.isArray(variants) || variants.length === 0) return null;
      const waistIndex = names.findIndex((n) => /waist/i.test(n));
      const inseamIndex = names.findIndex((n) => /inseam/i.test(n));
      const match = variants.find((v) =>
        (waistIndex < 0 || !this.#sizes.waist || String(v.options[waistIndex]) === this.#sizes.waist) &&
        (inseamIndex < 0 || !this.#sizes.inseam || String(v.options[inseamIndex]) === this.#sizes.inseam)
      );
      return (match || variants[0]).id;
    } catch {
      return null;
    }
  }

  async #addToCart(button) {
    this.#showError('');

    const id = this.#variantId();
    const url = this.dataset.cartAddUrl;
    if (!id || !url) {
      this.#showError(this.dataset.errorAdd);
      return;
    }

    const p = this.#picks();
    const d = this.dataset;
    const properties = {
      [d.labelFit || 'Fit']: this.#sizes.fit,
      [d.labelDenim || 'Denim']: p.denim,
      [d.labelThread || 'Thread']: p.thread,
      [d.labelRivet || 'Rivets']: p.rivet,
      [d.labelWaist || 'Waistband']: this.#sizes.waist || d.notSelected || 'To be confirmed',
      [d.labelInseam || 'Inseam']: this.#sizes.inseam || d.notSelected || 'To be confirmed',
    };

    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1, properties }] }),
      });
      if (!response.ok) throw new Error(String(response.status));
      window.location.href = d.cartUrl || '/';
    } catch {
      this.#showError(d.errorAdd);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-order-builder')) customElements.define('s-order-builder', SOrderBuilder);
